import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  paginatedPcmpRequestMineSchema,
  paginatedPcmpRequestQueueSchema,
  pcmpRequestCategoriesListSchema,
  pcmpRequestCountsSchema,
  pcmpRequestSubmitResultSchema,
  type PaginatedPcmpRequestMine,
  type PaginatedPcmpRequestQueue,
  type PcmpRequestBatchesParams,
  type PcmpRequestCounts,
  type PcmpRequestQueueParams,
  type PcmpRequestSubmitPayload,
  type PcmpRequestSubmitResult,
  type PcmpUrgentRequestsParams,
} from '../types/pcmpRequest.types';
import type { PcmpRequestCategory, PcmpRequestAppliesTo } from '../types/pcmpRequestCategory.types';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';
import type { PcmpRequestedParams } from '../types/pcmpRequested.types';
import {
  pcmpUrgentBatchesSchema,
  pcmpUrgentBatchProposalsSchema,
  pcmpUrgentRequestsSchema,
  type PcmpUrgentBatches,
  type PcmpUrgentBatchProposals,
  type PcmpUrgentRequests,
} from '../types/pcmpUrgent.types';

// Everything a request screen reads or changes sits under one key, so a
// submit, an act or a hold refreshes the lists and badges together.
export const pcmpRequestQueryKeys = {
  all: () => ['pcmp-requests'] as const,
  queues: () => [...pcmpRequestQueryKeys.all(), 'queue'] as const,
  queue: (params: PcmpRequestQueueParams) => [...pcmpRequestQueryKeys.queues(), params] as const,
  counts: (filters: PcmpQueueFilters) =>
    [...pcmpRequestQueryKeys.all(), 'counts', filters] as const,
  categories: (appliesTo: PcmpRequestAppliesTo | undefined) =>
    [...pcmpRequestQueryKeys.all(), 'categories', appliesTo ?? 'all'] as const,
  batches: (params: PcmpRequestBatchesParams) =>
    [...pcmpRequestQueryKeys.all(), 'batches', params] as const,
  batch: (batchNo: number, params: PcmpRequestBatchesParams) =>
    [...pcmpRequestQueryKeys.all(), 'batch', batchNo, params] as const,
  urgentRequests: (params: PcmpUrgentRequestsParams) =>
    [...pcmpRequestQueryKeys.all(), 'urgent-requests', params] as const,
  requested: (params: PcmpRequestedParams) =>
    [...pcmpRequestQueryKeys.all(), 'requested', params] as const,
  requestedCounts: (filters: PcmpQueueFilters) =>
    [...pcmpRequestQueryKeys.all(), 'requested-counts', filters] as const,
  report: (batchNo: number) => [...pcmpRequestQueryKeys.all(), 'report', batchNo] as const,
};

/** The Standard and Disbursed tabs: the proposals a request can still be raised on. */
export const pcmpRequestQueueQueryOptions = (params: PcmpRequestQueueParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpRequestQueue> => {
      const res = await apiClient.get(API.pcmpRequest.queue, { params });
      return paginatedPcmpRequestQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The My Requests tab: the requests I raised, each with its request. */
export const pcmpRequestMineQueryOptions = (params: PcmpRequestQueueParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpRequestMine> => {
      const res = await apiClient.get(API.pcmpRequest.queue, { params });
      return paginatedPcmpRequestMineSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpRequestCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.counts(filters),
    queryFn: async (): Promise<PcmpRequestCounts> => {
      const res = await apiClient.get(API.pcmpRequest.queueCounts, { params: filters });
      return pcmpRequestCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The active categories a requester may pick (needs the request-create right). */
export const pcmpRequestCategoriesForSubmitQueryOptions = (
  appliesTo: PcmpRequestAppliesTo | undefined,
) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.categories(appliesTo),
    queryFn: async (): Promise<PcmpRequestCategory[]> => {
      const res = await apiClient.get(API.pcmpRequest.categories, {
        params: appliesTo ? { appliesTo } : undefined,
      });
      return pcmpRequestCategoriesListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const pcmpUrgentBatchesQueryOptions = (params: PcmpRequestBatchesParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.batches(params),
    queryFn: async (): Promise<PcmpUrgentBatches> => {
      const res = await apiClient.get(API.pcmpRequest.batches, { params });
      return pcmpUrgentBatchesSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

// Fresh on every visit: stage, publish and rollback by approvers change it.
export const pcmpUrgentBatchQueryOptions = (batchNo: number, params: PcmpRequestBatchesParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.batch(batchNo, params),
    queryFn: async (): Promise<PcmpUrgentBatchProposals> => {
      const res = await apiClient.get(API.pcmpRequest.batchProposals(batchNo), { params });
      return pcmpUrgentBatchProposalsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

/** The Urgent Requests tab: my Urgent Payment requests day by day, newest first. */
export const pcmpUrgentRequestsQueryOptions = (params: PcmpUrgentRequestsParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.urgentRequests(params),
    queryFn: async (): Promise<PcmpUrgentRequests> => {
      const res = await apiClient.get(API.pcmpRequest.urgentRequests, { params });
      return pcmpUrgentRequestsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export async function submitPcmpRequests(
  payload: PcmpRequestSubmitPayload,
): Promise<PcmpRequestSubmitResult> {
  const res = await apiClient.post(API.pcmpRequest.submit, payload);
  return pcmpRequestSubmitResultSchema.parse(res.data);
}
